import { useEffect, useRef, useState, useMemo, useCallback } from 'react'
import { AlphaTabApi, Settings, NotationElement } from '@coderline/alphatab'
import { useHorizontalDrag } from '../hooks/useHorizontalDrag'

// Extract Score type from alphaTab's scoreLoaded event
type ScoreLoadedHandler = Parameters<AlphaTabApi['scoreLoaded']['on']>[0]
type Score = Parameters<ScoreLoadedHandler>[0]

/**
 * AlphaTab Multi-Track Rendering Notes:
 * =====================================
 * AlphaTab supports rendering multiple tracks via `api.renderTracks([track1, track2, ...])`,
 * but does NOT provide CSS classes or data attributes to identify which track each note belongs to.
 *
 * For per-track note coloring, we use a multi-instance approach:
 * - Each track/layer renders in its own AlphaTabViewer instance
 * - CSS classes (via className prop) apply colors to the entire instance
 * - Layer visibility is controlled by show/hide of entire instances
 *
 * See: /docs/alphatab-multi-track-investigation.md for full details
 */

/** Layer type for track categorization */
type LayerType = 'melody' | 'chords' | 'bass' | 'fills' | 'unassigned'

/** Layer visibility state */
interface LayerVisibility {
  melody: boolean
  chords: boolean
  bass: boolean
  fills: boolean
}

interface AlphaTabViewerProps {
  /** URL to the Guitar Pro file or raw file data */
  scoreUrl?: string
  scoreData?: Uint8Array | ArrayBuffer
  /** Callback when the API is initialized */
  onReady?: (api: AlphaTabApi) => void
  /** Callback when score is loaded */
  onScoreLoaded?: (score: Score) => void
  /** Callback when selected track changes */
  onTrackChange?: (trackIndex: number, trackName: string) => void
  /** CSS class name for layer styling (e.g., "layer-melody" for blue notes) */
  className?: string
  /** External ref to access the AlphaTab API for synchronized playback */
  apiRef?: React.MutableRefObject<AlphaTabApi | null>
  /** Function to get the layer type for a track index */
  getTrackLayer?: (trackIndex: number) => LayerType
  /** Current layer visibility state (controls which tracks appear in dropdown) */
  layerVisibility?: LayerVisibility
  /** Function to set the layer type for a track */
  setTrackLayer?: (trackIndex: number, layer: LayerType) => void
  /** Controlled track index - when set, component will select this track */
  controlledTrackIndex?: number
  /** Key to force re-evaluation of controlled track even if index is same */
  controlledTrackKey?: number
  /** Hide the playback controls toolbar (use when external controls manage playback) */
  hidePlaybackControls?: boolean
  /** Mute MIDI audio playback (cursor still moves, but no sound from AlphaTab) */
  muteAudio?: boolean
  /** Layout mode: 'page' (default, vertical) or 'horizontal' (horizontal scrolling with auto-scroll) */
  layoutMode?: 'page' | 'horizontal'
  /** Override display scale (default: 1.0 for page, 0.8 for horizontal) */
  scale?: number
  /** External control for notation visibility (when hidePlaybackControls is true) */
  externalShowNotation?: boolean
  /** External control for tab visibility (when hidePlaybackControls is true) */
  externalShowTab?: boolean
}

// Track info for display
interface TrackInfo {
  index: number
  name: string
  instrument: string
}

// MIDI program number to instrument name (simplified)
function getInstrumentName(program: number): string {
  const instruments: Record<number, string> = {
    0: 'Piano', 24: 'Acoustic Guitar', 25: 'Steel Guitar',
    26: 'Jazz Guitar', 27: 'Clean Guitar', 28: 'Muted Guitar',
    29: 'Overdrive Guitar', 30: 'Distortion Guitar', 31: 'Harmonics',
    32: 'Acoustic Bass', 33: 'Electric Bass', 34: 'Pick Bass',
    35: 'Fretless Bass', 36: 'Slap Bass 1', 37: 'Slap Bass 2'
  }
  return instruments[program] || `Program ${program}`
}

// Layer colors for track dropdown color-coding
const LAYER_COLORS: Record<LayerType, string> = {
  melody: '#3b82f6',     // blue
  chords: '#a855f7',     // purple
  bass: '#ef4444',       // red
  fills: '#22c55e',      // green
  unassigned: 'inherit', // default text color
}

export function AlphaTabViewer({ scoreUrl, scoreData, onReady, onScoreLoaded, onTrackChange, className, apiRef: externalApiRef, getTrackLayer, layerVisibility, setTrackLayer, controlledTrackIndex, controlledTrackKey, hidePlaybackControls = false, muteAudio = false, layoutMode = 'page', scale, externalShowNotation, externalShowTab }: AlphaTabViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const internalApiRef = useRef<AlphaTabApi | null>(null)
  const apiRef = externalApiRef || internalApiRef
  const [isPlaying, setIsPlaying] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Display controls state
  const [zoom, setZoom] = useState(100) // percentage: 50-200%
  const [showNotation, setShowNotation] = useState(externalShowNotation ?? true)
  const [showTab, setShowTab] = useState(externalShowTab ?? true)

  // Enable horizontal drag for horizontal layout mode
  useHorizontalDrag(containerRef, layoutMode === 'horizontal')

  // Sync with external props when they change
  useEffect(() => {
    if (externalShowNotation !== undefined) {
      setShowNotation(externalShowNotation)
    }
  }, [externalShowNotation])

  useEffect(() => {
    if (externalShowTab !== undefined) {
      setShowTab(externalShowTab)
    }
  }, [externalShowTab])

  // Compute the effective stave profile from external or internal state
  const effectiveShowNotation = externalShowNotation ?? showNotation
  const effectiveShowTab = externalShowTab ?? showTab

  // StaveProfile values: 0=Default, 1=ScoreTab (both), 2=Score only, 3=Tab only
  const effectiveStaveProfile = (() => {
    if (effectiveShowNotation && !effectiveShowTab) return 2 // Score only
    if (!effectiveShowNotation && effectiveShowTab) return 3 // Tab only
    return 1 // ScoreTab (both)
  })()

  // Check if we're in the problematic tab-only + horizontal mode
  const isTabOnlyHorizontal = effectiveStaveProfile === 3 && layoutMode === 'horizontal'

  // Refs to hold fresh values for props used in the main init effect,
  // so the effect only re-runs when scoreUrl/scoreData changes.
  const layoutModeRef = useRef(layoutMode)
  layoutModeRef.current = layoutMode
  const muteAudioRef = useRef(muteAudio)
  muteAudioRef.current = muteAudio
  const scaleRef = useRef(scale)
  scaleRef.current = scale
  const effectiveStaveProfileRef = useRef(effectiveStaveProfile)
  effectiveStaveProfileRef.current = effectiveStaveProfile
  const isTabOnlyHorizontalRef = useRef(isTabOnlyHorizontal)
  isTabOnlyHorizontalRef.current = isTabOnlyHorizontal
  const onReadyRef = useRef(onReady)
  onReadyRef.current = onReady
  const onScoreLoadedRef = useRef(onScoreLoaded)
  onScoreLoadedRef.current = onScoreLoaded
  const onTrackChangeRef = useRef(onTrackChange)
  onTrackChangeRef.current = onTrackChange

  // Note: We rely on the parent component changing the key prop to force a re-mount
  // when notation/tab visibility changes. This is needed because AlphaTab crashes
  // when dynamically switching to tab-only mode in horizontal layout.

  // Track selection state
  const [tracks, setTracks] = useState<TrackInfo[]>([])
  const [selectedTrackIndex, setSelectedTrackIndex] = useState<number>(0)

  // Playback controls state
  const [tempo, setTempo] = useState(100) // percentage: 10-100%
  const [isMuted, setIsMuted] = useState(false)

  // Compute visible tracks based on layer visibility
  // If no layer filtering props provided, show all tracks
  const visibleTracks = useMemo(() => {
    if (!getTrackLayer || !layerVisibility) {
      return tracks
    }
    return tracks.filter((track) => {
      const layer = getTrackLayer(track.index)
      // 'unassigned' tracks are always visible
      if (layer === 'unassigned') {
        return true
      }
      // Check if the track's layer is visible
      return layerVisibility[layer]
    })
  }, [tracks, getTrackLayer, layerVisibility])

  const handleTrackChange = useCallback((trackIndex: number) => {
    if (!apiRef.current || !apiRef.current.score) return

    const track = apiRef.current.score.tracks[trackIndex]
    if (track) {
      setSelectedTrackIndex(trackIndex)
      apiRef.current.renderTracks([track])
      // Notify parent of track change
      onTrackChangeRef.current?.(trackIndex, track.name || `Track ${trackIndex + 1}`)
    }
  }, [apiRef])

  // Auto-select first visible track if current selection becomes hidden
  useEffect(() => {
    // Only run if we have filtering enabled and tracks available
    if (!getTrackLayer || !layerVisibility || visibleTracks.length === 0) {
      return
    }
    // Check if currently selected track is still visible
    const isCurrentTrackVisible = visibleTracks.some(
      (track) => track.index === selectedTrackIndex
    )
    if (!isCurrentTrackVisible && visibleTracks.length > 0) {
      // Auto-select the first visible track
      const firstVisible = visibleTracks[0]
      handleTrackChange(firstVisible.index)
    }
  }, [visibleTracks, selectedTrackIndex, getTrackLayer, layerVisibility, handleTrackChange])

  useEffect(() => {
    if (!containerRef.current) return

    try {
      // Configure alphaTab settings
      const settings = new Settings()
      settings.core.engine = 'svg'
      settings.core.enableLazyLoading = false
      // Use absolute URL for font directory to work correctly in web workers
      // Web workers resolve relative URLs from the worker script location, not the document
      const fontDir = `${window.location.origin}/font/`
      settings.core.fontDirectory = fontDir
      // Point to the worker file in the public folder (copied from node_modules)
      // This is needed because Vite doesn't bundle alphaTab's worker separately
      settings.core.scriptFile = `${window.location.origin}/alphaTab.worker.mjs`
      settings.player.enablePlayer = true
      settings.player.enableCursor = true
      settings.player.enableUserInteraction = true
      // Layout mode settings
      // LayoutMode: 0 = Page (default, vertical), 1 = Horizontal
      // ScrollMode: 0 = Off, 1 = Continuous, 2 = OffScreen
      // Note: AlphaTab crashes with horizontal layout + tab-only mode, so we use page layout as workaround
      if (layoutModeRef.current === 'horizontal' && !isTabOnlyHorizontalRef.current) {
        settings.display.layoutMode = 1 // Horizontal
        settings.player.scrollMode = 3 // Smooth: constant smooth scrolling (not jumping)
        settings.player.nativeBrowserSmoothScroll = false // Use custom animation for smooth scroll
        settings.player.scrollSpeed = 300 // Scroll animation speed in ms
        // Negative offset pushes cursor toward center/right of viewport.
        // At ~50% of container width, cursor sits in the second half of screen.
        settings.player.scrollOffsetX = -(containerRef.current?.clientWidth ? Math.floor(containerRef.current.clientWidth * 0.4) : 400)
        settings.player.scrollElement = containerRef.current // Required for scrolling to work
        settings.display.scale = scaleRef.current ?? 0.8 // Default slightly smaller to fit notation + tabs
        // Hide score title/subtitle in horizontal mode (CapsuleViewer shows these separately)
        settings.notation.elements.set(NotationElement.ScoreTitle, false)
        settings.notation.elements.set(NotationElement.ScoreSubTitle, false)
        settings.notation.elements.set(NotationElement.ScoreArtist, false)
        settings.notation.elements.set(NotationElement.ScoreAlbum, false)
        settings.notation.elements.set(NotationElement.ScoreWords, false)
        settings.notation.elements.set(NotationElement.ScoreMusic, false)
        settings.notation.elements.set(NotationElement.ScoreCopyright, false)
      } else if (isTabOnlyHorizontalRef.current) {
        // Tab-only in horizontal mode crashes, use page layout with single row workaround
        settings.display.layoutMode = 0 // Page layout (workaround)
        settings.player.scrollMode = 0
        settings.display.scale = scaleRef.current ?? 0.8
        // Hide title elements
        settings.notation.elements.set(NotationElement.ScoreTitle, false)
        settings.notation.elements.set(NotationElement.ScoreSubTitle, false)
        settings.notation.elements.set(NotationElement.ScoreArtist, false)
        settings.notation.elements.set(NotationElement.ScoreAlbum, false)
        settings.notation.elements.set(NotationElement.ScoreWords, false)
        settings.notation.elements.set(NotationElement.ScoreMusic, false)
        settings.notation.elements.set(NotationElement.ScoreCopyright, false)
      } else {
        settings.display.layoutMode = 0 // Page (default)
        settings.player.scrollMode = 0 // Off
      }
      // Set stave profile based on external props (if provided) or default to both
      // StaveProfile: 0=Default, 1=ScoreTab (both), 2=Score only, 3=Tab only
      settings.display.staveProfile = effectiveStaveProfileRef.current
      // Hide tempo marker (BPM display) from the score - we show tempo in our own controls
      settings.notation.elements.set(NotationElement.EffectTempo, false)

      // Customize fonts and colors via JSON settings API
      // (Font/Color classes are internal to alphaTab, so we use fillFromJson)
      settings.fillFromJson({
        display: {
          // Reduce top padding (default is [35, 35] = [left-right, top-bottom])
          padding: [35, 10, 35, 10],
          resources: {
            // Emphasize tab numbers: larger and bold (default is 14px regular)
            tablatureFont: 'bold 16px Arial, sans-serif',
            // Make all tab numbers fully black (bass voice defaults to semi-transparent)
            secondaryGlyphColor: '#000000',
            // Increase chord name size (default is italic 12px serif)
            elementFonts: {
              effectChordNames: 'bold 16px Georgia, serif',
            },
          },
        },
      } as unknown as Parameters<Settings['fillFromJson']>[0])

      // Soundfont for audio playback
      settings.player.soundFont = 'https://cdn.jsdelivr.net/npm/@coderline/alphatab@latest/dist/soundfont/sonivox.sf2'

      // Initialize the API
      const api = new AlphaTabApi(containerRef.current, settings)

      // Setup event listeners
      api.renderStarted.on(() => {
        setIsLoading(true)
        setError(null)
      })

      api.renderFinished.on(() => {
        setIsLoading(false)
      })

      api.error.on((e) => {
        console.error('alphaTab error:', e)
        setError(e.message || 'Failed to load score')
        setIsLoading(false)
      })

      api.playerStateChanged.on((args) => {
        setIsPlaying(args.state === 1) // 1 = playing
      })

      api.scoreLoaded.on((score) => {
        // Extract track info
        const trackInfos: TrackInfo[] = score.tracks.map((track: { name: string; playbackInfo?: { program: number } }, index: number) => ({
          index,
          name: track.name || `Track ${index + 1}`,
          instrument: getInstrumentName(track.playbackInfo?.program || 0)
        }))
        setTracks(trackInfos)
        setSelectedTrackIndex(0)

        // Render first track by default
        if (score.tracks.length > 0) {
          api.renderTracks([score.tracks[0]])
          // Notify parent of initial track selection
          onTrackChangeRef.current?.(0, score.tracks[0].name || 'Track 1')
        }

        onScoreLoadedRef.current?.(score)
      })

      // Load the score
      if (scoreUrl) {
        api.load(scoreUrl)
      } else if (scoreData) {
        api.load(scoreData)
      }

      apiRef.current = api

      // Apply mute setting if requested
      if (muteAudioRef.current) {
        api.masterVolume = 0
      }

      onReadyRef.current?.(api)

      // Cleanup
      return () => {
        api.destroy()
      }
    } catch (err) {
      console.error('Failed to initialize alphaTab:', err)
      setError(err instanceof Error ? err.message : 'Failed to initialize')
      setIsLoading(false)
    }
  }, [scoreUrl, scoreData])

  const handlePlayPause = () => {
    if (!apiRef.current) return

    if (isPlaying) {
      apiRef.current.pause()
    } else {
      apiRef.current.play()
    }
  }

  const handleStop = () => {
    if (!apiRef.current) return
    apiRef.current.stop()
  }

  // Display control handlers
  const handleZoomIn = () => {
    if (!apiRef.current) return
    const newZoom = Math.min(zoom + 10, 200) // Max 200%
    setZoom(newZoom)
    apiRef.current.settings.display.scale = newZoom / 100
    apiRef.current.updateSettings()
    apiRef.current.render()
  }

  const handleZoomOut = () => {
    if (!apiRef.current) return
    const newZoom = Math.max(zoom - 10, 50) // Min 50%
    setZoom(newZoom)
    apiRef.current.settings.display.scale = newZoom / 100
    apiRef.current.updateSettings()
    apiRef.current.render()
  }

  // Update stave profile based on notation/tab visibility
  // Button ON = element visible, Button OFF = element hidden
  const updateStaveProfile = (notation: boolean, tab: boolean) => {
    if (!apiRef.current) return

    // StaveProfile values: 0=Default, 1=ScoreTab (both), 2=Score only, 3=Tab only
    let profile = 1 // ScoreTab (both) by default
    if (notation && !tab) {
      profile = 2 // Score only (notation visible, tab hidden)
    } else if (!notation && tab) {
      profile = 3 // Tab only (tab visible, notation hidden)
    }
    // If both true → profile 1 (both visible)
    // If both false → shouldn't happen due to guards

    apiRef.current.settings.display.staveProfile = profile
    apiRef.current.updateSettings()
    apiRef.current.render()
  }

  const handleToggleNotation = () => {
    if (!apiRef.current) return
    // Prevent hiding both notation and TAB
    if (showNotation && !showTab) return

    const newShowNotation = !showNotation
    setShowNotation(newShowNotation)
    updateStaveProfile(newShowNotation, showTab)
  }

  const handleToggleTab = () => {
    if (!apiRef.current) return
    // Prevent hiding both notation and TAB
    if (!showNotation && showTab) return

    const newShowTab = !showTab
    setShowTab(newShowTab)
    updateStaveProfile(showNotation, newShowTab)
  }

  // Respond to controlled track index changes from parent
  // The key forces re-evaluation even when the track index is the same
  useEffect(() => {
    if (controlledTrackIndex !== undefined) {
      // Only change if the track exists
      if (apiRef.current?.score?.tracks[controlledTrackIndex]) {
        handleTrackChange(controlledTrackIndex)
      }
    }
  }, [controlledTrackIndex, controlledTrackKey, handleTrackChange])

  // Tempo control handlers
  const handleTempoDown = () => {
    if (!apiRef.current) return
    const newTempo = Math.max(tempo - 10, 10) // Min 10%
    setTempo(newTempo)
    apiRef.current.playbackSpeed = newTempo / 100
  }

  const handleTempoUp = () => {
    if (!apiRef.current) return
    const newTempo = Math.min(tempo + 10, 100) // Max 100%
    setTempo(newTempo)
    apiRef.current.playbackSpeed = newTempo / 100
  }

  // Mute toggle handler
  const handleToggleMute = () => {
    if (!apiRef.current) return
    const newMuted = !isMuted
    setIsMuted(newMuted)
    apiRef.current.masterVolume = newMuted ? 0 : 1
  }

  return (
    <div className={`alphatab-viewer ${className || ''}`} style={{
      width: '100%',
      minWidth: 0,
      height: layoutMode === 'horizontal' ? '100%' : undefined,
      display: layoutMode === 'horizontal' ? 'flex' : undefined,
      flexDirection: layoutMode === 'horizontal' ? 'column' : undefined,
    }}>
      {/* Unified Controls Toolbar - hidden when hidePlaybackControls is true */}
      {!hidePlaybackControls && <div style={{
        padding: 'var(--space-sm) var(--space-md)',
        background: 'var(--color-bg-elevated)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)',
        marginBottom: 'var(--space-md)',
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-md)',
        flexWrap: 'wrap'
      }}>
        {/* Playback Controls */}
        <div style={{ display: 'flex', gap: 'var(--space-xs)', alignItems: 'center' }}>
          <button
            data-testid="play-button"
            onClick={handlePlayPause}
            disabled={isLoading || !!error}
            style={{
              width: '36px',
              height: '36px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: isPlaying ? 'var(--color-accent)' : 'var(--color-bg-card)',
              border: '1px solid var(--color-border)',
              borderRadius: '50%',
              color: isPlaying ? 'white' : 'var(--color-text)',
              cursor: isLoading || error ? 'not-allowed' : 'pointer',
              fontWeight: 500,
              fontSize: '14px',
              opacity: isLoading || error ? 0.5 : 1,
              transition: 'var(--transition-fast)'
            }}
            title={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying ? '⏸' : '▶'}
          </button>

          <button
            onClick={handleStop}
            disabled={isLoading || !!error}
            style={{
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'var(--color-bg-card)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--color-text-secondary)',
              cursor: isLoading || error ? 'not-allowed' : 'pointer',
              fontSize: '12px',
              opacity: isLoading || error ? 0.5 : 1,
              transition: 'var(--transition-fast)'
            }}
            title="Stop"
          >
            ⏹
          </button>

          {/* Mute Button */}
          <button
            data-testid="mute-button"
            onClick={handleToggleMute}
            disabled={isLoading || !!error}
            style={{
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: isMuted ? 'var(--color-accent-subtle)' : 'var(--color-bg-card)',
              border: `1px solid ${isMuted ? 'var(--color-accent)' : 'var(--color-border)'}`,
              borderRadius: 'var(--radius-sm)',
              color: isMuted ? 'var(--color-accent)' : 'var(--color-text-secondary)',
              cursor: isLoading || error ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              opacity: isLoading || error ? 0.5 : 1,
              transition: 'var(--transition-fast)'
            }}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? '🔇' : '🔊'}
          </button>
        </div>

        {/* Divider */}
        <div style={{ width: '1px', height: '24px', background: 'var(--color-border)' }} />

        {/* Tempo Controls */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
          <span style={{ fontSize: '10px', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Tempo</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
            <button
              data-testid="tempo-down-button"
              onClick={handleTempoDown}
              disabled={isLoading || !!error || tempo <= 10}
              aria-label="Decrease tempo"
              style={{
                width: '28px',
                height: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--color-bg-card)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--color-text-secondary)',
                cursor: (isLoading || error || tempo <= 10) ? 'not-allowed' : 'pointer',
                transition: 'var(--transition-fast)',
                opacity: (isLoading || error || tempo <= 10) ? 0.5 : 1,
                fontWeight: 500
              }}
            >
              −
            </button>
            <span style={{
              fontSize: '12px',
              fontWeight: 500,
              color: tempo < 100 ? 'var(--color-accent)' : 'var(--color-text-muted)',
              minWidth: '44px',
              textAlign: 'center'
            }}>
              {tempo}%
            </span>
            <button
              data-testid="tempo-up-button"
              onClick={handleTempoUp}
              disabled={isLoading || !!error || tempo >= 100}
              aria-label="Increase tempo"
              style={{
                width: '28px',
                height: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--color-bg-card)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--color-text-secondary)',
                cursor: (isLoading || error || tempo >= 100) ? 'not-allowed' : 'pointer',
                transition: 'var(--transition-fast)',
                opacity: (isLoading || error || tempo >= 100) ? 0.5 : 1,
                fontWeight: 500
              }}
            >
              +
            </button>
          </div>
        </div>

        {/* Divider */}
        <div style={{ width: '1px', height: '24px', background: 'var(--color-border)' }} />

        {/* Zoom Controls */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
          <span style={{ fontSize: '10px', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>Zoom</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
            <button
              data-testid="zoom-out-button"
              onClick={handleZoomOut}
              disabled={isLoading || !!error || zoom <= 50}
              aria-label="Zoom out"
              style={{
                width: '28px',
                height: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--color-bg-card)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--color-text-secondary)',
                cursor: (isLoading || error || zoom <= 50) ? 'not-allowed' : 'pointer',
                transition: 'var(--transition-fast)',
                opacity: (isLoading || error || zoom <= 50) ? 0.5 : 1,
                fontWeight: 500
              }}
            >
              −
            </button>
            <span style={{
              fontSize: '12px',
              fontWeight: 500,
              color: 'var(--color-text-muted)',
              minWidth: '36px',
              textAlign: 'center'
            }}>
              {zoom}%
            </span>
            <button
              data-testid="zoom-in-button"
              onClick={handleZoomIn}
              disabled={isLoading || !!error || zoom >= 200}
              aria-label="Zoom in"
              style={{
                width: '28px',
                height: '28px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--color-bg-card)',
                border: '1px solid var(--color-border)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--color-text-secondary)',
                cursor: (isLoading || error || zoom >= 200) ? 'not-allowed' : 'pointer',
                transition: 'var(--transition-fast)',
                opacity: (isLoading || error || zoom >= 200) ? 0.5 : 1,
                fontWeight: 500
              }}
            >
              +
            </button>
          </div>
        </div>

        {/* Divider */}
        <div style={{ width: '1px', height: '24px', background: 'var(--color-border)' }} />

        {/* Notation/TAB Toggles */}
        <div style={{ display: 'flex', gap: 'var(--space-xs)' }}>
          <button
            data-testid="toggle-notation"
            onClick={handleToggleNotation}
            disabled={isLoading || !!error || (showNotation && !showTab)}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: 500,
              background: showNotation ? 'var(--color-accent-subtle)' : 'var(--color-bg-card)',
              border: `1px solid ${showNotation ? 'var(--color-accent)' : 'var(--color-border)'}`,
              borderRadius: 'var(--radius-sm)',
              color: showNotation ? 'var(--color-accent)' : 'var(--color-text-secondary)',
              cursor: (isLoading || error || (showNotation && !showTab)) ? 'not-allowed' : 'pointer',
              transition: 'var(--transition-fast)',
              opacity: (isLoading || error || (showNotation && !showTab)) ? 0.5 : 1
            }}
          >
            Notation
          </button>
          <button
            data-testid="toggle-tab"
            onClick={handleToggleTab}
            disabled={isLoading || !!error || (!showNotation && showTab)}
            style={{
              padding: '6px 12px',
              fontSize: '12px',
              fontWeight: 500,
              background: showTab ? 'var(--color-accent-subtle)' : 'var(--color-bg-card)',
              border: `1px solid ${showTab ? 'var(--color-accent)' : 'var(--color-border)'}`,
              borderRadius: 'var(--radius-sm)',
              color: showTab ? 'var(--color-accent)' : 'var(--color-text-secondary)',
              cursor: (isLoading || error || (!showNotation && showTab)) ? 'not-allowed' : 'pointer',
              transition: 'var(--transition-fast)',
              opacity: (isLoading || error || (!showNotation && showTab)) ? 0.5 : 1
            }}
          >
            TAB
          </button>
        </div>

        {/* Track Selector - only show if multiple visible tracks */}
        {visibleTracks.length > 1 && (
          <>
            <div style={{ width: '1px', height: '24px', background: 'var(--color-border)' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
              <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>Track:</span>
              <select
                data-testid="track-selector"
                value={selectedTrackIndex}
                onChange={(e) => handleTrackChange(parseInt(e.target.value, 10))}
                disabled={isLoading || !!error}
                style={{
                  padding: '4px 8px',
                  fontSize: '12px',
                  background: 'var(--color-bg-card)',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-sm)',
                  color: 'var(--color-text)',
                  cursor: isLoading || error ? 'not-allowed' : 'pointer',
                  minWidth: '140px'
                }}
              >
                {visibleTracks.map((track) => {
                  const layer = getTrackLayer?.(track.index) ?? 'unassigned'
                  return (
                    <option
                      key={track.index}
                      value={track.index}
                      style={{ color: LAYER_COLORS[layer] }}
                    >
                      {track.name} ({track.instrument})
                    </option>
                  )
                })}
              </select>
            </div>
          </>
        )}

        {/* Layer Assignment - show when setTrackLayer is available */}
        {setTrackLayer && tracks.length > 0 && (
          <>
            <div style={{ width: '1px', height: '24px', background: 'var(--color-border)' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-xs)' }}>
              <span style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>Layer:</span>
              <select
                data-testid="layer-selector"
                value={getTrackLayer?.(selectedTrackIndex) ?? 'unassigned'}
                onChange={(e) => setTrackLayer(selectedTrackIndex, e.target.value as LayerType)}
                disabled={isLoading || !!error}
                style={{
                  padding: '4px 8px',
                  fontSize: '12px',
                  background: 'var(--color-bg-card)',
                  border: `1px solid ${LAYER_COLORS[getTrackLayer?.(selectedTrackIndex) ?? 'unassigned'] === 'inherit' ? 'var(--color-border)' : LAYER_COLORS[getTrackLayer?.(selectedTrackIndex) ?? 'unassigned']}`,
                  borderRadius: 'var(--radius-sm)',
                  color: LAYER_COLORS[getTrackLayer?.(selectedTrackIndex) ?? 'unassigned'],
                  cursor: isLoading || error ? 'not-allowed' : 'pointer',
                  fontWeight: 500
                }}
              >
                <option value="melody" style={{ color: LAYER_COLORS.melody }}>Melody</option>
                <option value="chords" style={{ color: LAYER_COLORS.chords }}>Chords</option>
                <option value="bass" style={{ color: LAYER_COLORS.bass }}>Bass</option>
                <option value="fills" style={{ color: LAYER_COLORS.fills }}>Fills</option>
                <option value="unassigned" style={{ color: 'var(--color-text-muted)' }}>—</option>
              </select>
            </div>
          </>
        )}

        {/* Loading indicator - always present to prevent layout shift */}
        <span style={{
          color: 'var(--color-text-muted)',
          fontSize: '13px',
          marginLeft: 'auto',
          opacity: isLoading ? 1 : 0,
          transition: 'opacity 0.2s ease',
        }}>
          Loading...
        </span>
      </div>}

      {/* Error Display */}
      {error && (
        <div style={{
          padding: 'var(--space-md)',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: 'var(--radius-md)',
          color: '#ef4444',
          marginBottom: 'var(--space-md)'
        }}>
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Notation Container */}
      <div
        ref={containerRef}
        data-testid="notation-container"
        style={{
          background: '#ffffff',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-lg)',
          minHeight: layoutMode === 'horizontal' ? undefined : '400px',
          height: layoutMode === 'horizontal' ? '100%' : undefined,
          // Tab-only in horizontal mode uses page layout but constrain to single row height
          maxHeight: isTabOnlyHorizontal ? '180px' : undefined,
          flex: layoutMode === 'horizontal' ? 1 : undefined,
          width: '100%',
          maxWidth: '100%',
          overflow: layoutMode === 'horizontal' ? 'auto' : 'auto',
          overflowY: layoutMode === 'horizontal' ? 'hidden' : 'auto',
          // Enable horizontal scrolling for tab-only page layout
          overflowX: isTabOnlyHorizontal ? 'auto' : undefined,
          position: 'relative'
        }}
      />

      {/* alphaTab cursor styles */}
      <style>{`
        .at-cursor-bar {
          background: transparent !important;
        }
        .at-cursor-beat {
          background: rgba(64, 64, 255, 0.75) !important;
          width: 3px !important;
        }
        .at-highlight * {
          fill: #ff8800 !important;
        }
        .at-surface {
          position: relative;
        }
      `}</style>
    </div>
  )
}
